import React, { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { Button } from 'react-bootstrap'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faPen, faTrash } from '@fortawesome/pro-solid-svg-icons'

import { sanitizeHtml } from 'helpers/sanitizeHtml'
import InputLabel from 'components/ui/forms/field/input-label/InputLabel'
import CheckBoxRadioButtonInput from 'components/ui/forms/field/checkbox-radiobutton/CheckBoxRadioButtonInput'
import { Alphabet, breakLinesFix } from 'helpers/others.helper'
import { IQuestionData } from 'models/question.model'
import { QuestionOptionForm, QuestionForm } from 'models/question.model'
import styles from './EvaluationQuestionCard.module.scss'

interface IEvaluationQuestionCard {
  question: IQuestionData
  onEdit: () => void
  onDelete: () => void
  number: number
}

const defaultValues: QuestionForm = {
  questionId: 0,
  text: '',
  link: { url: '', type: 0 },
  options: [
    { text: '', checked: false },
    { text: '', checked: true },
  ],
  order: 0,
}

const EvaluationQuestionCard: React.FC<IEvaluationQuestionCard> = ({
  question,
  onEdit,
  onDelete,
  number,
}) => {
  const { t } = useTranslation()
  const [questionData, setQuestionData] = useState<QuestionForm>(defaultValues)

  useEffect(() => {
    const { name, url, urlType, questionableId, options } = question
    const optionsAux = options.map(
      ({ label, checked }): QuestionOptionForm => ({
        text: label,
        checked,
      })
    )

    setQuestionData({
      questionId: questionableId,
      text: name,
      link: {
        url: url,
        type: urlType,
      },
      options: optionsAux,
    })
  }, [question])

  const truncate = (text: string) => {
    if (text.length < 80) return text
    return text.substring(0, 80) + '...'
  }

  const getChecked = (options: QuestionOptionForm[]) => {
    const index = options.findIndex(({ checked }) => checked)
    return index >= 0 ? Alphabet[index] : ''
  }

  return (
    <div className={`${styles.container}`}>
      <div className="d-flex justify-content-between align-items-center mb-2">
        <InputLabel
          label={`${t(
            'administration.blocks.type.evaluation.question.singular'
          )} ${number}`}
        />
        <div className={styles.actionButtons}>
          <Button className={`ml-3`} onClick={onEdit}>
            <FontAwesomeIcon size={'xs'} icon={faPen} />
          </Button>
          <Button className={`ml-2`} onClick={onDelete}>
            <FontAwesomeIcon size={'xs'} icon={faTrash} />
          </Button>
        </div>
      </div>
      <div
        className={styles.questionName}
        dangerouslySetInnerHTML={{
          __html: breakLinesFix(sanitizeHtml(questionData.text)),
        }}
      />
      {questionData.link.url.trim() && (
        <div className={`w-100 ${styles.url} `}>
          <div>
            <b>{t('administration.blocks.type.evaluation.link')}: </b>{' '}
            <span>{truncate(questionData.link.url)}</span>
          </div>
        </div>
      )}
      <div className={`mt-1 ${styles.checkboxContainer}`}>
        {questionData.options?.map(({ text }, index: number) => (
          <CheckBoxRadioButtonInput
            key={'created-question-' + questionData.questionId + index}
            name={'created-question-' + questionData.questionId + index}
            value={true}
            checked={false}
            inline={true}
            type={'radio'}
            label={`${Alphabet[index]}) ${text}`}
            disabled
          />
        ))}
      </div>
      <div className={`${styles.correctAnswer} mt-1`}>
        {t('administration.blocks.type.evaluation.correctAnswer')}:{' '}
        {getChecked(questionData.options || [])}
      </div>
    </div>
  )
}

export default EvaluationQuestionCard
